import { useState, useMemo } from 'react';
import { Plus, Pencil, Trash2, Check, X, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { TaxRate, TaxRateFormData, UK_VAT_RATES } from '@/types/tax-rate';

export default function TaxRatesPage() {
  const [taxRates, setTaxRates] = useState<TaxRate[]>([
    {
      id: '1',
      name: UK_VAT_RATES.STANDARD.name,
      rate: UK_VAT_RATES.STANDARD.rate,
      description: UK_VAT_RATES.STANDARD.description,
      isDefault: true,
      isActive: true,
      createdDate: new Date(),
      modifiedDate: new Date(),
    },
    {
      id: '2',
      name: UK_VAT_RATES.REDUCED.name,
      rate: UK_VAT_RATES.REDUCED.rate,
      description: UK_VAT_RATES.REDUCED.description,
      isDefault: false,
      isActive: true,
      createdDate: new Date(),
      modifiedDate: new Date(),
    },
    {
      id: '3',
      name: UK_VAT_RATES.ZERO.name,
      rate: UK_VAT_RATES.ZERO.rate,
      description: UK_VAT_RATES.ZERO.description,
      isDefault: false,
      isActive: true,
      createdDate: new Date(),
      modifiedDate: new Date(),
    },
  ]);

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedTaxRate, setSelectedTaxRate] = useState<TaxRate | null>(null);
  const [formData, setFormData] = useState<TaxRateFormData>({
    name: '',
    rate: 0,
    description: '',
    isDefault: false,
    isActive: true,
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Validation
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    if (formData.rate < 0 || formData.rate > 100) {
      errors.rate = 'Rate must be between 0 and 100';
    }

    // Check for duplicate name (excluding current item when editing)
    const isDuplicate = taxRates.some(
      (rate) =>
        rate.name.toLowerCase() === formData.name.toLowerCase() &&
        rate.id !== selectedTaxRate?.id
    );
    if (isDuplicate) {
      errors.name = 'A tax rate with this name already exists';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handlers
  const handleAdd = () => {
    setFormData({
      name: '',
      rate: 0,
      description: '',
      isDefault: false,
      isActive: true,
    });
    setFormErrors({});
    setIsAddDialogOpen(true);
  };

  const handleEdit = (taxRate: TaxRate) => {
    setSelectedTaxRate(taxRate);
    setFormData({
      name: taxRate.name,
      rate: taxRate.rate,
      description: taxRate.description || '',
      isDefault: taxRate.isDefault,
      isActive: taxRate.isActive,
    });
    setFormErrors({});
    setIsEditDialogOpen(true);
  };

  const handleDelete = (taxRate: TaxRate) => {
    setSelectedTaxRate(taxRate);
    setIsDeleteDialogOpen(true);
  };

  const handleSaveAdd = () => {
    if (!validateForm()) return;

    const newTaxRate: TaxRate = {
      id: Date.now().toString(),
      name: formData.name,
      rate: formData.rate,
      description: formData.description,
      isDefault: formData.isDefault,
      isActive: formData.isActive,
      createdDate: new Date(),
      modifiedDate: new Date(),
    };

    // If setting as default, remove default from others
    if (formData.isDefault) {
      setTaxRates((prev) =>
        prev.map((rate) => ({ ...rate, isDefault: false }))
      );
    }

    setTaxRates((prev) => [...prev, newTaxRate]);
    setIsAddDialogOpen(false);

    toast.success(`Tax rate added: ${formData.name}`);
  };

  const handleSaveEdit = () => {
    if (!validateForm() || !selectedTaxRate) return;

    // If setting as default, remove default from others
    if (formData.isDefault) {
      setTaxRates((prev) =>
        prev.map((rate) =>
          rate.id === selectedTaxRate.id
            ? { ...rate, isDefault: false }
            : { ...rate, isDefault: false }
        )
      );
    }

    setTaxRates((prev) =>
      prev.map((rate) =>
        rate.id === selectedTaxRate.id
          ? {
              ...rate,
              name: formData.name,
              rate: formData.rate,
              description: formData.description,
              isDefault: formData.isDefault,
              isActive: formData.isActive,
              modifiedDate: new Date(),
            }
          : rate
      )
    );

    setIsEditDialogOpen(false);
    setSelectedTaxRate(null);

    toast.success(`Tax rate updated: ${formData.name}`);
  };

  const handleConfirmDelete = () => {
    if (!selectedTaxRate) return;

    // Check if it's the default rate
    if (selectedTaxRate.isDefault) {
      toast.error('Cannot delete the default tax rate. Set another rate as default first.');
      setIsDeleteDialogOpen(false);
      return;
    }

    setTaxRates((prev) => prev.filter((rate) => rate.id !== selectedTaxRate.id));
    setIsDeleteDialogOpen(false);
    setSelectedTaxRate(null);

    toast.success(`Tax rate deleted: ${selectedTaxRate.name}`);
  };

  const handleToggleActive = (taxRate: TaxRate) => {
    // Don't allow deactivating the default rate
    if (taxRate.isDefault && taxRate.isActive) {
      toast.error('Cannot deactivate the default tax rate.');
      return;
    }

    setTaxRates((prev) =>
      prev.map((rate) =>
        rate.id === taxRate.id
          ? { ...rate, isActive: !rate.isActive, modifiedDate: new Date() }
          : rate
      )
    );

    toast.success(`${taxRate.name} has been ${taxRate.isActive ? 'deactivated' : 'activated'}.`);
  };

  const handleSetDefault = (taxRate: TaxRate) => {
    if (!taxRate.isActive) {
      toast.error('Cannot set an inactive tax rate as default. Activate it first.');
      return;
    }

    setTaxRates((prev) =>
      prev.map((rate) => ({
        ...rate,
        isDefault: rate.id === taxRate.id,
        modifiedDate: rate.id === taxRate.id ? new Date() : rate.modifiedDate,
      }))
    );

    toast.success(`${taxRate.name} is now the default tax rate.`);
  };

  // Stats
  const stats = useMemo(() => {
    return {
      total: taxRates.length,
      active: taxRates.filter((r) => r.isActive).length,
      inactive: taxRates.filter((r) => !r.isActive).length,
      default: taxRates.find((r) => r.isDefault)?.name || 'None',
    };
  }, [taxRates]);

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Tax Rates</h1>
          <p className="text-muted-foreground mt-1">
            Configure tax rates for inventory valuation and reporting
          </p>
        </div>
        <Button onClick={handleAdd}>
          <Plus className="h-4 w-4 mr-2" />
          Add Tax Rate
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card border rounded-lg p-4">
          <div className="text-sm text-muted-foreground">Total Rates</div>
          <div className="text-2xl font-bold">{stats.total}</div>
        </div>
        <div className="bg-card border rounded-lg p-4">
          <div className="text-sm text-muted-foreground">Active</div>
          <div className="text-2xl font-bold text-green-600">{stats.active}</div>
        </div>
        <div className="bg-card border rounded-lg p-4">
          <div className="text-sm text-muted-foreground">Inactive</div>
          <div className="text-2xl font-bold text-gray-600">{stats.inactive}</div>
        </div>
        <div className="bg-card border rounded-lg p-4">
          <div className="text-sm text-muted-foreground">Default Rate</div>
          <div className="text-lg font-bold truncate">{stats.default}</div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-blue-900">
          <p className="font-medium mb-1">About Tax Rates</p>
          <p>
            Tax rates are used for inventory valuation and cost analysis. They are NOT used for
            creating sales orders or invoices (that's done in Access Financials). The default tax
            rate will be automatically applied to new stock items.
          </p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-card border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Rate</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Default</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {taxRates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  No tax rates configured. Click "Add Tax Rate" to get started.
                </TableCell>
              </TableRow>
            ) : (
              taxRates.map((taxRate) => (
                <TableRow key={taxRate.id}>
                  <TableCell className="font-medium">{taxRate.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{taxRate.rate}%</Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground max-w-xs truncate">
                    {taxRate.description || '-'}
                  </TableCell>
                  <TableCell>
                    {taxRate.isDefault ? (
                      <Badge variant="default" className="bg-green-600">
                        <Check className="h-3 w-3 mr-1" />
                        Default
                      </Badge>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSetDefault(taxRate)}
                        disabled={!taxRate.isActive}
                      >
                        Set as Default
                      </Button>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={taxRate.isActive}
                        onCheckedChange={() => handleToggleActive(taxRate)}
                        disabled={taxRate.isDefault}
                      />
                      <span className="text-sm">
                        {taxRate.isActive ? (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">
                            Inactive
                          </Badge>
                        )}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(taxRate)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(taxRate)}
                        disabled={taxRate.isDefault}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Tax Rate</DialogTitle>
            <DialogDescription>
              Create a new tax rate for inventory valuation and cost analysis.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">
                Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                placeholder="e.g., Standard VAT"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              {formErrors.name && (
                <p className="text-sm text-red-500">{formErrors.name}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="rate">
                Rate (%) <span className="text-red-500">*</span>
              </Label>
              <Input
                id="rate"
                type="number"
                min="0"
                max="100"
                step="0.01"
                placeholder="e.g., 20"
                value={formData.rate}
                onChange={(e) => setFormData({ ...formData, rate: parseFloat(e.target.value) || 0 })}
              />
              {formErrors.rate && (
                <p className="text-sm text-red-500">{formErrors.rate}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="e.g., Standard rate for most goods and services"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="isDefault">Set as default tax rate</Label>
              <Switch
                id="isDefault"
                checked={formData.isDefault}
                onCheckedChange={(checked) => setFormData({ ...formData, isDefault: checked })}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="isActive">Active</Label>
              <Switch
                id="isActive"
                checked={formData.isActive}
                onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveAdd}>Add Tax Rate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Tax Rate</DialogTitle>
            <DialogDescription>
              Update the tax rate details.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">
                Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="edit-name"
                placeholder="e.g., Standard VAT"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              {formErrors.name && (
                <p className="text-sm text-red-500">{formErrors.name}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-rate">
                Rate (%) <span className="text-red-500">*</span>
              </Label>
              <Input
                id="edit-rate"
                type="number"
                min="0"
                max="100"
                step="0.01"
                placeholder="e.g., 20"
                value={formData.rate}
                onChange={(e) => setFormData({ ...formData, rate: parseFloat(e.target.value) || 0 })}
              />
              {formErrors.rate && (
                <p className="text-sm text-red-500">{formErrors.rate}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                placeholder="e.g., Standard rate for most goods and services"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="edit-isDefault">Set as default tax rate</Label>
              <Switch
                id="edit-isDefault"
                checked={formData.isDefault}
                onCheckedChange={(checked) => setFormData({ ...formData, isDefault: checked })}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="edit-isActive">Active</Label>
              <Switch
                id="edit-isActive"
                checked={formData.isActive}
                onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                disabled={selectedTaxRate?.isDefault}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Tax Rate</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedTaxRate?.name}"? This action cannot be undone.
              {selectedTaxRate?.isDefault && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-900">
                  <AlertCircle className="h-4 w-4 inline mr-1" />
                  Cannot delete the default tax rate. Set another rate as default first.
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={selectedTaxRate?.isDefault}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
